import React, { useState } from 'react';
import { getFullImageUrl } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const BeforeAfterReport = ({ complaint, onUpdateRequested, isStaff }) => {
  const [selectedImg, setSelectedImg] = useState(null);
  const { t } = useLanguage();

  if (!complaint) return null;

  const beforeImg = getFullImageUrl(complaint.complaint_image);
  const afterImg = getFullImageUrl(complaint.resolution_image);

  const handlePrintReport = () => {
    window.print();
  };

  return (
    <div className="card glass-card border-0 p-4 mb-4 shadow-sm position-relative overflow-hidden">
      {/* Printable Report Header (Only visible when printing) */}
      <div className="d-none d-print-block text-center mb-4 border-bottom pb-3">
        <h2 className="fw-bold text-uppercase brand-font">{t('officialReportHeader')}</h2>
        <p className="text-muted mb-1">{t('publicWorksDesk')}</p>
        <div className="d-flex justify-content-between small text-dark mt-3">
          <span><strong>{t('tableTicketNum')}:</strong> {complaint.ticket_number}</span>
          <span><strong>{t('tableStatus')}:</strong> {t(`status_${complaint.status.replace(/\s+/g, '')}`, complaint.status)}</span>
          <span><strong>{t('dateGenerated')}:</strong> {new Date().toLocaleDateString()}</span>
        </div>
      </div>

      {/* Card Title & Print Button */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4 pb-2 border-bottom border-light">
        <div>
          <h5 className="brand-font fw-bold text-dark mb-1">
            <i className="bi bi-images text-primary me-2"></i> {t('beforeAfterTitle')}
          </h5>
          <p className="text-muted extra-small mb-0">
            {t('beforeAfterSubtitle')}
          </p>
        </div>

        <button 
          onClick={handlePrintReport} 
          className="btn btn-outline-dark btn-sm rounded-pill px-3 shadow-sm d-print-none"
        >
          <i className="bi bi-printer-fill me-1 text-primary"></i> {t('printOfficialReport')}
        </button>
      </div>

      {/* Side-by-side Visual Comparison Container */}
      <div className="row g-4 align-items-stretch">
        {/* Left Box: Before (Complaint Image) */}
        <div className="col-md-6">
          <div className="h-100 p-3 rounded-4 bg-light border d-flex flex-column justify-content-between position-relative">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="badge bg-danger px-3 py-2 text-uppercase font-monospace">
                <i className="bi bi-exclamation-circle-fill me-1"></i> {t('beforeIssue')}
              </span>
              <span className="text-muted extra-small">
                {new Date(complaint.created_at).toLocaleDateString()}
              </span>
            </div>

            <div className="my-2 text-center flex-grow-1 d-flex align-items-center justify-content-center bg-white rounded-3 p-2 border overflow-hidden" style={{ minHeight: '220px', maxHeight: '320px' }}>
              {beforeImg ? (
                <img
                  src={beforeImg}
                  alt="Before Complaint"
                  className="img-fluid rounded-3 cursor-pointer object-fit-cover w-100 h-100"
                  style={{ maxHeight: '280px', objectFit: 'contain', cursor: 'pointer' }}
                  onClick={() => setSelectedImg(beforeImg)}
                />
              ) : (
                <div className="py-4 text-muted">
                  <i className="bi bi-camera-fill fs-1 d-block mb-2 text-secondary opacity-50"></i>
                  <span className="small italic">{t('noBeforePhoto')}</span>
                </div>
              )}
            </div>

            <div className="mt-2 pt-2 border-top">
              <small className="text-muted d-block fw-semibold mb-1">{t('customerDescription')}</small>
              <p className="text-dark small mb-0 text-truncate-3" style={{ whiteSpace: 'pre-wrap' }}>
                {complaint.description}
              </p>
            </div>
          </div>
        </div>

        {/* Right Box: After (Solution Image) */}
        <div className="col-md-6">
          <div className="h-100 p-3 rounded-4 bg-success bg-opacity-10 border border-success border-opacity-25 d-flex flex-column justify-content-between position-relative">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="badge bg-success px-3 py-2 text-uppercase font-monospace">
                <i className="bi bi-check-circle-fill me-1"></i> {t('afterSolution')}
              </span>
              {['Resolved', 'Closed'].includes(complaint.status) ? (
                <span className="badge bg-success text-white extra-small">{t('verifiedFixed')}</span>
              ) : (
                <span className="badge bg-warning text-dark extra-small">{t('inProgress')}</span>
              )}
            </div>

            <div className="my-2 text-center flex-grow-1 d-flex align-items-center justify-content-center bg-white rounded-3 p-2 border overflow-hidden" style={{ minHeight: '220px', maxHeight: '320px' }}>
              {afterImg ? (
                <img
                  src={afterImg}
                  alt="After Solution"
                  className="img-fluid rounded-3 cursor-pointer object-fit-cover w-100 h-100"
                  style={{ maxHeight: '280px', objectFit: 'contain', cursor: 'pointer' }}
                  onClick={() => setSelectedImg(afterImg)}
                />
              ) : (
                <div className="py-4 text-muted">
                  <i className="bi bi-image-fill fs-1 d-block mb-2 text-secondary opacity-50"></i>
                  <span className="small italic">
                    {['Resolved', 'Closed'].includes(complaint.status)
                      ? t('noAfterPhoto')
                      : t('pendingResolutionPhoto')}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-2 pt-2 border-top">
              <small className="text-success fw-bold d-block mb-1">
                <i className="bi bi-journal-check me-1"></i> {t('agentResolutionRemarks')}
              </small>
              <p className="text-dark small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {complaint.resolution_notes || (
                  <span className="text-muted italic">{t('noResolutionNotes')}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Metadata Block */}
      <div className="d-none d-print-block mt-4 pt-4 border-top">
        <div className="row g-3 small">
          <div className="col-6">
            <p className="mb-1"><strong>{t('submittedBy')}:</strong> {complaint.customer_name} ({complaint.customer_email || complaint.guest_phone})</p>
            <p className="mb-1"><strong>{t('tableAreaLoc')}:</strong> {complaint.area_name || t('notSpecified')} {complaint.sub_area_name ? `(${complaint.sub_area_name})` : ''}</p>
            <p className="mb-1"><strong>{t('tableDept')}:</strong> {complaint.department_name || t('unassigned')}</p>
          </div>
          <div className="col-6">
            <p className="mb-1"><strong>{t('tableAssignedEmp')}:</strong> {complaint.assigned_employee_name || complaint.agent_name || 'Staff Team'}</p>
            <p className="mb-1"><strong>{t('category')} & {t('tablePriority')}:</strong> {complaint.category} | {t(`priority_${complaint.priority}`, complaint.priority)}</p>
            <div className="mt-4 pt-3 border-top text-end">
              <span className="d-inline-block border-bottom border-dark px-4 pb-1 fw-bold">{t('authorizedSignoff')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Preview Modal */}
      {selectedImg && (
        <div 
          className="modal fade show d-block" 
          tabIndex="-1" 
          style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1060 }}
          onClick={() => setSelectedImg(null)}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-content bg-transparent border-0 text-center">
              <div className="modal-header border-0 justify-content-end p-2">
                <button 
                  type="button" 
                  className="btn-close btn-close-white fs-4" 
                  onClick={() => setSelectedImg(null)}
                ></button>
              </div>
              <div className="modal-body p-2">
                <img 
                  src={selectedImg} 
                  alt="Enlarged View" 
                  className="img-fluid rounded-3 shadow-lg" 
                  style={{ maxHeight: '85vh', objectFit: 'contain' }} 
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeforeAfterReport;
